import { Component, Element, h, Host, Prop, State, Watch } from "@stencil/core";
import pfx from "../../../utils/style-class-prefix-helper";
import { BULLET_LIST_SIZE_LARGE } from "../constants/bullet-list-constants";
import { ICONSIZE_NORMAL, ICONSIZE_SMALL } from "../../icon/constants/icon-constants";
import sanitizeHtml from "sanitize-html";
import { allowClassesAndLinks } from "../../../utils/sanitize-helper";
import { StyledComponent } from "../../../decorators/styled-component";

// import { InitLogging } from "../../../decorators/init-logging";

/**
 * @slot info-icon - Holds a single eon-ui-info-icon element
 * @slot html-text - Holds the contents of the text element
 */
@Component({
  tag: "eon-ui-bullet-list-item",
  styleUrl: "bullet-list-item.scss",
  shadow: true
})
export class BulletListItem {
  // @InitLogging() sentryTransaction;
  private isLargeSize: boolean;
  private processedText: string;

  @State() textShadow: boolean = false;
  @State() isInfoIconAvailable: boolean;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEonUiBulletListItemElement;

  /**
   * The icon which should be shown
   */
  @Prop({ reflect: true, mutable: true }) iconName: string;

  /**
   * The bullet list item text
   * If you need HTML support, provide the text via the slot "html-text".
   */
  @Prop() text: string;

  /**
   * Set the highlight color for each item
   */
  @Prop({ reflect: true, mutable: true }) highlight: boolean = false;

  /**
   * Specifies whether the content has the normal font-size of copy text or if it should be rendered with large styles.
   * The value is inherited from the parent component "bullet-list"
   */
  @Prop({ reflect: true }) size;

  /**
   * The icon should be centered with fixed width
   */
  @Prop() centeredIcon: boolean = false;

  /**
   * The info icons should be aligned at the extreme end of bullet list item
   */
  @Prop({ reflect: true, mutable: true }) alignInfoIcon: boolean = false;

  /**
   * A boolean property that can be used to disable line breaks.
   */
  @Prop() disableLineWrap: boolean = false;

  componentWillLoad() {
    this.handleSize();
    this.handleTextShadow();

    this.isInfoIconAvailable = !!this.hostElement.querySelector('[slot="info-icon"]');
    this.updateProcessedText();
  }

  private handleTextShadow(): void {
    const parentNode = this.hostElement.parentNode as HTMLElement;
    if (parentNode.tagName.toLowerCase() === "eon-ui-bullet-list") {
      this.textShadow = parentNode.getAttribute("text-shadow") === "true";
    }
  }

  @Watch("text")
  updateProcessedText() {
    const slashN = "\\n";
    if (this.text && !this.disableLineWrap) {
      this.processedText = this.text.split(slashN).join("<br />");
    } else {
      this.processedText = this.text;
    }
  }

  @Watch("size")
  handleSize() {
    this.isLargeSize = this.size === BULLET_LIST_SIZE_LARGE;
  }

  render() {
    return (
      <Host>
        <div
          class={pfx({
            "bullet-list-item-wrapper": true,
            "full-width": this.isInfoIconAvailable && this.alignInfoIcon,
            highlight: this.highlight,
            large: this.isLargeSize
          })}
        >
          <eon-ui-icon
            size={this.isLargeSize ? ICONSIZE_NORMAL : ICONSIZE_SMALL}
            aria-hidden="true"
            class={pfx("icon-content")}
            name={this.iconName}
            noSpace={!this.centeredIcon}
          ></eon-ui-icon>
          <div
            class={pfx({
              "bullet-list-item-title-wrapper": true,
              "full-width": this.isInfoIconAvailable && this.alignInfoIcon
            })}
          >
            <div
              class={pfx({
                "bullet-list-item-title": true,
                "text-shadow": this.textShadow,
                "info-icon-display": this.isInfoIconAvailable,
                "align-info-icon": this.isInfoIconAvailable && this.alignInfoIcon
              })}
            >
              <span
                innerHTML={sanitizeHtml(
                  !this.hostElement.querySelector('[slot="html-text"]') ? this.processedText : undefined,
                  allowClassesAndLinks
                )}
              >
                <slot name="html-text"></slot>
              </span>
              {this.isInfoIconAvailable && (
                <span
                  class={pfx({
                    "bullet-list-info-icon": true,
                    "remove-right-spacing": this.isInfoIconAvailable && this.alignInfoIcon
                  })}
                >
                  <slot name="info-icon" />
                </span>
              )}
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
